import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

/**
 * canonicalizeRole — normalize legacy/alias role strings to canonical values
 * before any comparison. Applied once at the top of ProtectedRoute.
 */
function canonicalizeRole(role) {
  if (!role) return role;
  switch (role) {
    case 'super_admin': case 'superuser':
      return 'superadmin';
    case 'provider_clinician':
      return 'provider_clinician';
    case 'nurse_reviewer':
      return 'nurse_reviewer';
    case 'clinic':
      return 'site_clinic';
    case 'sponsor':
      return 'sponsor_cro';
    case 'fellow':
      return 'clincore';
    case 'investor':
      return 'investor_demo';
    default:
      return role;
  }
}

/**
 * ProtectedRoute — wraps routes that require authentication and/or specific roles.
 *
 * Demo Isolation (P0-5):
 *   demo@curegate.ai → ONLY /investor-dashboard (read-only fixtures)
 *   All staff/admin routes are BLOCKED for demo users server-side.
 *
 * Patient Verification (P0-1):
 *   requiresVerification=true → unverified patients are redirected to /unverified-account
 *   Uses a hard server-side session check — not just client-side user object fields.
 *
 * Sponsor Pending (P0-3):
 *   requiresSponsorApproval=true → pending_approval sponsors are redirected to /sponsor-pending
 *   Uses a server-side profile status check against user_profiles.status.
 *
 * Props:
 *   children              — the protected page component
 *   redirectTo            — where to send unauthenticated users (default: '/staff-login')
 *   allowedRoles          — array of roles that can access this route; empty = any authenticated user
 *   superuserBypass       — if true, superusers always get access regardless of allowedRoles
 *   demoAllowed           — if true, Investor/demo users can access this route (sandbox routes)
 *   productionOnly        — if true, only non-demo production users can access this route
 *   demoBlocked           — if true, demo/investor users are explicitly blocked (staff-only routes)
 *   requiresVerification  — if true, unverified patients are redirected to /unverified-account
 *   requiresSponsorApproval — if true, pending_approval sponsors are redirected to /sponsor-pending
 */
const ProtectedRoute = ({
  children,
  redirectTo = '/staff-login',
  allowedRoles = [],
  superuserBypass = true,
  demoAllowed = false,
  productionOnly = false,
  demoBlocked = false,
  requiresVerification = false,
  requiresSponsorApproval = false,
}) => {
  const { user, userRole: rawUserRole, userProfile, isSuperuser, isInvestor, isDemoMode, loading, isEmailVerified } = useAuth();
  const location = useLocation();

  // Canonicalize role before any comparison
  const userRole = canonicalizeRole(rawUserRole);

  // ── P0-1: Server-side verification state ──────────────────────────────────
  const [verificationChecked, setVerificationChecked] = useState(!requiresVerification);
  const [isVerified, setIsVerified] = useState(false);

  // ── P0-3: Server-side sponsor approval state ──────────────────────────────
  const [sponsorApprovalChecked, setSponsorApprovalChecked] = useState(!requiresSponsorApproval);
  const [isSponsorApproved, setIsSponsorApproved] = useState(false);
  const [sponsorPendingState, setSponsorPendingState] = useState(null);

  useEffect(() => {
    if (!requiresVerification || isDemoMode || isInvestor || !user) {
      setVerificationChecked(true);
      setIsVerified(true);
      return;
    }

    // Skip for staff / non-patient roles (use canonical names)
    const staffRoles = ['superadmin', 'staff', 'site_clinic', 'sponsor_cro', 'investor_demo', 'clincore'];
    if (staffRoles?.includes(userRole)) {
      setIsVerified(true);
      setVerificationChecked(true);
      return;
    }

    // Prefer AuthContext profile flag (app-level, survives mailer_autoconfirm).
    // Fall back to a direct profile read so a race during login cannot open the dashboard.
    const applyProfile = (profile) => {
      const ok =
        profile?.email_verified === true ||
        profile?.verification_status === 'verified';
      setIsVerified(!!ok);
      setVerificationChecked(true);
    };

    if (userProfile) {
      applyProfile(userProfile);
      return;
    }

    supabase
      ?.from('user_profiles')
      ?.select('email_verified, verification_status, role')
      ?.eq('id', user?.id)
      ?.maybeSingle()
      ?.then(({ data }) => {
        if (data && staffRoles?.includes(canonicalizeRole(data?.role))) {
          setIsVerified(true);
          setVerificationChecked(true);
          return;
        }
        applyProfile(data);
      })
      ?.catch(() => {
        // Fail closed for patient routes
        setIsVerified(!!isEmailVerified);
        setVerificationChecked(true);
      });
  }, [requiresVerification, user, userProfile, isDemoMode, isInvestor, userRole, isEmailVerified]);

  // ── P0-3: Sponsor pending approval check ──────────────────────────────────
  useEffect(() => {
    if (!requiresSponsorApproval || isDemoMode || isInvestor || !user) {
      setSponsorApprovalChecked(true);
      return;
    }

    // P0 Sprint 1 + P0-04: Staff/admin bypass — super_admin and staff NEVER go to /sponsor-pending
    // Accept both role === 'super_admin' and role_alias === 'super_admin'
    const bypassRoles = ['superadmin', 'staff'];
    const roleAlias = canonicalizeRole(user?.app_metadata?.role_alias || user?.user_metadata?.role_alias || null);
    if (bypassRoles?.includes(userRole) || bypassRoles?.includes(roleAlias) || isSuperuser) {
      setIsSponsorApproved(true);
      setSponsorApprovalChecked(true);
      return;
    }

    // Only enforce for sponsor customer roles
    const sponsorRoles = ['sponsor_cro'];
    if (!sponsorRoles?.includes(userRole)) {
      setIsSponsorApproved(true);
      setSponsorApprovalChecked(true);
      return;
    }

    // P0-3: Hard server-side check — query user_profiles.status directly.
    supabase
      ?.from('user_profiles')
      ?.select('status, organization, full_name')
      ?.eq('id', user?.id)
      ?.single()
      ?.then(({ data, error }) => {
        if (error || !data) {
          // On error, deny access conservatively
          setIsSponsorApproved(false);
          setSponsorPendingState({ email: user?.email });
          setSponsorApprovalChecked(true);
          return;
        }
        if (data?.status === 'pending_approval') {
          setIsSponsorApproved(false);
          setSponsorPendingState({
            email: user?.email,
            orgName: data?.organization || '',
          });
        } else {
          setIsSponsorApproved(true);
        }
        setSponsorApprovalChecked(true);
      })
      ?.catch(() => {
        setIsSponsorApproved(false);
        setSponsorPendingState({ email: user?.email });
        setSponsorApprovalChecked(true);
      });
  }, [requiresSponsorApproval, user, isDemoMode, isInvestor, userRole, isSuperuser]);

  if (loading || !verificationChecked || !sponsorApprovalChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0C' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Verifying access…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // ── P0 Sprint 1: Super admin bypass — evaluated BEFORE role checks ─────────
  // super_admin accessing /sponsor-dashboard always sees the staff-management view.
  // requiresSponsorApproval is irrelevant for super_admin — they are never pending.
  if (superuserBypass && isSuperuser) {
    return children;
  }

  // ── P0-1: Patient email verification gate ─────────────────────────────────
  if (requiresVerification && !isDemoMode && !isInvestor) {
    if (!isVerified) {
      return <Navigate to="/unverified-account" state={{ from: location, email: user?.email }} replace />;
    }
  }

  // ── P0-3: Sponsor pending approval gate ───────────────────────────────────
  // P0 Sprint 1: Only applies to unverified Sponsor/CRO customer accounts.
  // super_admin is already handled above and never reaches this gate.
  if (requiresSponsorApproval && !isDemoMode && !isInvestor) {
    if (!isSponsorApproved) {
      return (
        <Navigate
          to="/sponsor-pending"
          state={{ from: location, ...sponsorPendingState }}
          replace
        />
      );
    }
  }

  // ── P0-5: Strict demo isolation ───────────────────────────────────────────
  if (isDemoMode || isInvestor) {
    if (demoBlocked || productionOnly) {
      return <Navigate to="/investor-dashboard" replace />;
    }
    if (demoAllowed) {
      return children;
    }
    return <Navigate to="/investor-dashboard" replace />;
  }

  if (productionOnly && (isDemoMode || isInvestor)) {
    return <Navigate to="/" replace />;
  }

  // Canonicalize allowedRoles for comparison
  const canonicalAllowedRoles = allowedRoles?.map(canonicalizeRole);
  if (canonicalAllowedRoles?.length > 0 && !canonicalAllowedRoles?.includes(userRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0C' }}>
        <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white">Access Denied</h2>
          <p className="text-gray-400 text-sm">You don't have permission to access this page. Please contact your administrator if you believe this is an error.</p>
          <p className="text-gray-600 text-xs font-mono">Required role: {canonicalAllowedRoles?.join(', ')}</p>
          <a href="/" className="mt-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors">
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
